import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { AddDataSource, TemperatureDataState } from '@app/cnst/add-data.cnst'
import { usesBluetoothDevice } from '@app/cnst/hardware.cnst'
import { ROUTES } from '@app/cnst/nav.cnst'
import { AddDataPageService } from '@app/pages/add-data/add-data.page.service'
import { select } from '@app/srv/store.service'
import { distinctUntilDeeplyChanged } from '@app/util/distinctUntilDeeplyChanged'
import { App } from '@capacitor/app'
import { _Memo, pDelay } from '@naturalcycles/js-lib'
import { HardwareId } from '@naturalcycles/shared'
import { BehaviorSubject, combineLatestWith, filter, startWith, Subscription } from 'rxjs'
import { B1Service } from './b1.service.js'
import { BluetoothService, BluetoothStatus } from './bluetooth.service.js'
import { BluetoothThermometerService } from './bluetooth.thermometer.service.js'
import { EventService } from './event.service.js'
import { HardwareDeviceService } from './hardwareDevice.service.js'
import { NavService } from './nav.service.js'
import { T3Service } from './t3.service.js'
import { UebeService } from './uebe.service.js'

@Injectable({ providedIn: 'root' })
export class BluetoothConnectService {
  private addDataPageService = inject(AddDataPageService)
  private b1Service = inject(B1Service)
  private bluetoothService = inject(BluetoothService)
  private bluetoothThermometerService = inject(BluetoothThermometerService)
  private eventService = inject(EventService)
  private hardwareDeviceService = inject(HardwareDeviceService)
  private navService = inject(NavService)
  private t3Service = inject(T3Service)
  private uebeService = inject(UebeService)
  private router = inject(Router)

  private hwId$ = select(s => s.account.hwId)
  private hardwareDeviceId$ = select(s => s.hwDevice?.mac)
  private lastActive$ = select(s => s.userSettings.lastActive)

  private subscriptions: Subscription[] = []

  private scanningScheduled$ = new BehaviorSubject<boolean>(false)
  /** Used as a flag to disconnect when we won't do it automatically */
  private shouldDisconnect$ = new BehaviorSubject<boolean>(false)
  /** Used as a flag to check if we have synced the data from the band */
  private dataSyncedInSession$ = new BehaviorSubject<boolean>(false)

  @_Memo()
  public init(): void {
    this.hwId$
      .pipe(combineLatestWith(this.bluetoothService.inPairingFlow$))
      .subscribe(([hwId, inPairingFlow]) => {
        this.resetSubscriptions()

        // if we are in pairing flow we don't want to connect to the currently paired device
        if (inPairingFlow || !usesBluetoothDevice(hwId)) return

        this.setupSubscriptions()
      })
  }

  private setupSubscriptions(): void {
    this.subscriptions.push(
      this.hardwareDeviceId$
        .pipe(
          combineLatestWith(
            this.bluetoothService.bluetoothStatus$,
            this.lastActive$,
            this.hwId$,
            this.navService.navigationEnd$.pipe(startWith(undefined)),
          ),
          distinctUntilDeeplyChanged(),
          combineLatestWith(this.eventService.onResume$.pipe(startWith(1))),
        )
        .subscribe(async ([[deviceId, status, _lastActive, hwId, _route], _onResume]) => {
          if (!deviceId) return

          if (status === BluetoothStatus.FIRMWARE_UPDATE) {
            void this.bluetoothService.disconnect(deviceId)
            void this.bluetoothService.stopScan('firmware update')
            return
          }

          if (status === BluetoothStatus.BLUETOOTH_OFF && hwId === HardwareId.B1) {
            // Only place where this syncingState in the b1Service should be changed outside of the service
            this.b1Service.ongoingSyncingState.set(TemperatureDataState.BLUETOOTH_OFF)
          }

          if (status === BluetoothStatus.DISCONNECTED && !this.scanningScheduled$.value) {
            // wait before starting scanning again after disconnect
            this.scanningScheduled$.next(true)
            await pDelay(2000)

            this.scanningScheduled$.next(false)
            const { isActive } = await App.getState()
            if (!isActive) return

            // We don't want to restart scanning if we already synced data in this session (even empty)
            // unless we are on MyDevicePage, otherwise we will get into endless connection loop with the band
            // Here we use the getCurrentRoute() to check the route after the delay of 2 seconds and not the one which was triggered in the navigationEnd$

            const isOnMyDevicePage = this.navService
              .getCurrentRoute()
              .startsWith(ROUTES.MyDevicePage)

            // If device is B1, allow only one sync per session unless on MyDevicePage
            if (hwId === HardwareId.B1 && this.dataSyncedInSession$.value && !isOnMyDevicePage) {
              return
            }

            void this.bluetoothService.scanAndConnect(deviceId)
          }
        }),

      // Subscription which is disconnecting the bluetooth device when: DeviceId exists + BL is connected + should disconnect + not on device page
      this.hwId$
        .pipe(
          filter(hwId => [HardwareId.T3_THERMOMETER, HardwareId.B1].includes(hwId)),
          combineLatestWith(
            this.bluetoothService.connected$,
            this.shouldDisconnect$,
            this.hardwareDeviceId$,
            this.navService.navigationEnd$.pipe(startWith(undefined)),
          ),
        )
        .subscribe(async ([_, connected, shouldDisconnect, deviceId, route]) => {
          if (!deviceId) return
          if (!connected) return
          if (!shouldDisconnect) return
          if (route?.startsWith(ROUTES.MyDevicePage)) return

          await this.bluetoothService.disconnect(deviceId)
          this.shouldDisconnect$.next(false)
        }),

      this.eventService.onPause$
        .pipe(combineLatestWith(this.hardwareDeviceId$))
        .subscribe(([_onPause, deviceId]) => {
          if (!deviceId) return

          this.dataSyncedInSession$.next(false)

          if (this.bluetoothService.bluetoothStatus$.value === BluetoothStatus.CONNECTED) {
            void this.bluetoothService.disconnect(deviceId)
          } else if (this.bluetoothService.bluetoothStatus$.value === BluetoothStatus.SCANNING) {
            void this.bluetoothService.stopScan('App pause')
          }
        }),

      this.eventService.onResume$
        .pipe(combineLatestWith(this.hardwareDeviceId$))
        .subscribe(([_onResume, deviceId]) => {
          if (!deviceId) return

          this.dataSyncedInSession$.next(false)
        }),

      // on connection
      this.bluetoothService.connected$
        .pipe(combineLatestWith(this.hardwareDeviceId$, this.hwId$))
        .subscribe(([connected, deviceId, hwId]) => {
          if (!connected || !deviceId || !hwId) return

          // We rely on T3 name to identify the device but the plugin uses deviceId to sync data
          // So we should update this value if we see a new one
          // deviceId is possible to update on iOS update or iOS <> Android switch
          if (hwId === HardwareId.T3_THERMOMETER && deviceId !== connected.deviceId) {
            // Returning as we will call subscription again once deviceId is updated
            return void this.hardwareDeviceService.extendHardwareDevice({
              mac: connected.deviceId,
            })
          }

          void this.onConnected(hwId, deviceId)
        }),

      // on temperatures
      this.bluetoothThermometerService.temperatures$.subscribe(temperatures => {
        if (!temperatures.length || this.shouldNotOpenAddData) return

        void this.addDataPageService.openAddData({
          source: AddDataSource.BLUETOOTH,
          temperatures,
          skipPreview: true,
        })
      }),
    )
  }

  private resetSubscriptions(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
    this.subscriptions.length = 0 // this clears the array
  }

  private get shouldGetTemperatures(): boolean {
    const route = this.router.url

    if ([ROUTES.T3WalkthroughPage, ROUTES.UebeWalkthroughPage].includes(route)) return true
    if (route.startsWith(ROUTES.MyDevicePage)) return false

    return true
  }

  private get shouldNotOpenAddData(): boolean {
    const route = this.router.url

    if (
      // Places where we expect the user to measure and sync temperatures that should not go to Add data
      [ROUTES.CalibratePage, ROUTES.T3WalkthroughPage, ROUTES.UebeWalkthroughPage].includes(route)
    ) {
      return true
    }

    // Do not open Add data if we are in the morning router outlet
    const morningOutlet = document.querySelector('ion-router-outlet[name="morning"]')

    return !!morningOutlet?.children.length
  }

  private async onConnected(hwId: HardwareId, deviceId: string): Promise<void> {
    this.shouldDisconnect$.next(false)
    if (hwId === HardwareId.UEBE_THERMOMETER) {
      return void this.uebeService.startNotifications(deviceId, this.shouldGetTemperatures)
    }

    if (hwId === HardwareId.T3_THERMOMETER) {
      const deviceStatus = await this.t3Service.getDeviceStatus(deviceId)
      if (!this.shouldGetTemperatures) return

      await this.t3Service.getTemperatureLogs(deviceId, deviceStatus)
      this.shouldDisconnect$.next(true)
      return
    }

    if (hwId === HardwareId.B1) {
      // Sync band status and timestamp
      const hasDataToRead = await this.b1Service.connectedCallback()

      // Unlike thermometers, we should always sync measurement data if it is available
      if (hasDataToRead) {
        await this.b1Service.readMeasurementData()
      } else {
        this.b1Service.reportEmptyData()
      }

      this.shouldDisconnect$.next(true)

      // Set data as synced (first time syncing in session)
      this.dataSyncedInSession$.next(true)
      return
    }
  }
}
